/**
 * The saloon view's geometry: the mock's projection, as a module.
 *
 * ============================== WHERE EVERY NUMBER IS FROM =======================
 * `Poker assets/source/build-mock.py`, the builder of mock m11 — the picture Tim
 * approved. The plate is a FLAT top-down table image tilted with CSS (`rotateX(33deg)`
 * under `perspective: 2600px`), and everything that has to stay legible — plates,
 * cards, chips, bubbles, the board — is NOT tilted: it is placed in screen space at
 * the point where the tilted plate draws its felt coordinate. `project()` is that
 * point. It is the mock's function to the digit, and `tests/saloon-geometry.test.ts`
 * holds it to the builder's own output for thirteen points.
 *
 * All positions are in STAGE PIXELS — the mock's 1600 x 900 stage. `SaloonTable`
 * scales the stage uniformly to fit its box with one CSS length (`--u`, a stage pixel,
 * `min(100cqw / 1600, 100cqh / 900)`), so these numbers never change with the window,
 * exactly as the mock's `fit()` did but without a resize listener.
 *
 * Numbers that are NOT in the builder are marked PHASE 7, with the reason. The report's
 * geometry table lists every one, mock value → shipped value.
 * ===============================================================================
 */

/** Degrees the plate is tilted, and the perspective distance. build-mock.py: `TILT, D = 33, 2600`. */
export const TILT = 33;
export const D = 2600;
/** The stage the mock is drawn on. */
export const STAGE = { w: 1600, h: 900 } as const;
/** The tilted plane: its size and where its centre lands. build-mock.py: `PW, PH`, `CXS, CYS`. */
export const PLANE = { w: 1180, h: 850, cx: 800, cy: 505 } as const;
/** The felt ellipse, as fractions of the plate. build-mock.py: `FX, FY, RX, RY`. */
export const FELT = { fx: 0.5, fy: 0.505, rx: 0.41, ry: 0.4 } as const;

export interface Projected { x: number; y: number; s: number }

/**
 * Plate fraction (0..1, 0..1) → stage x, y and the depth scale there.
 *
 * build-mock.py `project()`, verbatim. It is also exactly what the browser does to the
 * plate: `rotateX(θ)` sends a point `y` from the plate's centre to `y cos θ` and lifts
 * it `y sin θ` toward or away from the viewer, and `perspective: D` scales it by
 * `D / (D − z)`. So an element placed here sits on the pixel where the tilted image
 * draws that part of the felt.
 */
export function project(fx: number, fy: number): Projected {
  const x = (fx - 0.5) * PLANE.w;
  const y = (fy - 0.5) * PLANE.h;
  const th = (TILT * Math.PI) / 180;
  const z = y * Math.sin(th);
  const s = D / (D - z);
  return { x: PLANE.cx + x * s, y: PLANE.cy + y * Math.cos(th) * s, s };
}

/** A point on the felt ellipse, `k` of the way out, at `deg` (0 = right, 90 = far side). build-mock.py `ell()`. */
export function ell(deg: number, k = 1): [number, number] {
  const a = (deg * Math.PI) / 180;
  return [FELT.fx + FELT.rx * k * Math.cos(a), FELT.fy - FELT.ry * k * Math.sin(a)];
}

/**
 * The top of the table's silhouette at each column of the plate, as a fraction of its
 * height — build-mock.py `silhouette_top()`, which reads the plate image's alpha
 * (`> 40`) down one pixel column. A client cannot read an image's alpha before it has
 * drawn it, so the columns are baked in here: the first opaque row of every one of the
 * plate's 1,100 columns, two base-36 digits each, from the same file
 * (`export/table/poker-table-green-topdown-1100.webp`, byte-identical to
 * `public/art/table/poker-topdown-1100.webp`), 792 rows tall. Exact, not interpolated —
 * the standing figures are placed off it, and the test holds them to the mock's pixels.
 */
const PLATE_PX = { w: 1100, h: 792 } as const;
const SILHOUETTE_ROWS =
  "m0acababa190909090908y8x8v8a89898884837q7q7p7p7k7k7b7875746w6w6w6w6r6q6p6p6n6k6d6d6d686868666361" +
  "61605p5o5o5o5o5k5j5i5f5d5c5c5c58585656555351504y4t4t4t4o4n4n4n4n4i4h4h4h4h4g4f49494545454545403z" +
  "3y3y3y3w3u3t3t3t3s3s3n3m3m3m3m3m3k3g3g3g3g3g3f3d3a3a3a3a353433333333333231302z2z2v2v2v2v2v2v2t2s" +
  "2s2r2r2r2q2k2k2k2k2k2k2j2i2h2h2h2g2f2f2f2e2c2b2b2a29292929272626252523232323222121201z1y1x1x1x1x" +
  "1r1q1q1q1q1q1q1q1q1q1q1q1p1o1k1k1k1k1k1k1k1h1h1h1e1e1e1e1e1e1e1e1e1e1e1d1c1b1a191919181818181818" +
  "1716151514141212121212121211111111110y0y0y0x0w0w0w0w0w0w0v0v0v0v0u0t0t0p0p0p0p0p0p0p0p0p0p0p0p0p" +
  "0p0p0p0p0n0m0m0m0l0l0l0l0l0l0l0k0k0k0k0k0k0j0j0j0i0h0h0h0h0e0d0d0d0d0d0d0d0d0d0d0d0d0d0d0d0d0d0d" +
  "0d0d0d0d0d0d0c0c0c0c0c0b0b0b0b0b0b0a0a0808080808080808080808080808080808080808080807070707070707" +
  "060606060606060505040404040404040201010101010101010101010101010101010101010101010101010101010101" +
  "010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101" +
  "010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101" +
  "010101010101010101010100000000000000000000000001020202020202020202020202020202020202020202020203" +
  "030303030303030303030303030303030303030303030303030303030304040404040404040404040404040404040404" +
  "040404040404040404040404040404040404040404040404040404040404040404040404040404040404040404050505" +
  "050505050505050505050505050505050505050505050505050505050505050506060606060606060707070707070707" +
  "0708080808080808080809090909090909090909090a0a0a0a0a0a0b0b0b0b0b0b0b0c0c0d0d0d0d0d0d0d0d0e0e0e0e" +
  "0e0f0f0f0f0f0g0g0g0g0h0h0h0h0h0i0i0i0i0i0j0j0k0k0k0k0k0l0l0l0l0l0m0m0m0n0n0n0n0o0o0p0p0p0p0q0q0q" +
  "0q0q0q0q0q0s0s0s0s0s0s0t0v0v0v0v0w0w0w0w0w0w0w0w0w0x0x0x1011111212121212121415151515151515161718" +
  "1818181818191a1b1c1d1e1e1e1e1f1f1f1f1f1f1f1f1f1f1f1k1k1k1k1k1k1m1m1m1m1n1o1o1q1q1q1r1r1t1u1v1w1w" +
  "1w1w1w1w1w1w1x1x232323232323232323242525292929292929292f2f2f2f2f2g2j2k2l2l2l2l2n2o2p2q2q2r2r2r2r" +
  "2r2r2s2x2x2y2y2y30323333333333343a3a3a3a3a3a3g3g3g3g3g3j3l3m3m3m3m3m3p3s3t3t3t3u3u3y3y3y3y3y3z45" +
  "45454545454a4e4e4e4e4h4i4m4n4n4n4n4t4t4u4u4u4u4u5252525252595c5c5c5c5c5c5j5o5o5o5o5p5y5y5y616161" +
  "686a6b6b6b6b6f6o6p6q6q6r6t7272727474757j7k7k7k7l7n8183838388898m8s8s8s8s8x9m9m9m9n9xb3b4";
export function silhouetteTop(fx: number): number {
  const col = Math.min(PLATE_PX.w - 1, Math.max(0, Math.floor(fx * PLATE_PX.w)));
  return parseInt(SILHOUETTE_ROWS.slice(col * 2, col * 2 + 2), 36) / PLATE_PX.h;
}

/* ------------------------------------------------------------- the seats --- */

/**
 * The opponents' bearings on the ellipse, clockwise from the hero.
 *
 * WHY THE MOCK'S `ell()` AND NOT `seatPositions()`. `seatPositions()` walks the WHOLE
 * ellipse by arc length from the hero at six o'clock, holding a gap for them. In the
 * saloon the hero is not on the ring at all — they stand off the felt, bottom-left, and
 * the bottom of the table is theirs and the action bar's — so every opponent has to
 * stand round the FAR half, facing the camera, the way m11 draws them. That is an arc,
 * not a ring, and the mock placed it by angle: 166°, 127°, 90°, 53°, 14° at six-max,
 * which is a span of 152° in steps of about 38°. Those five are used verbatim at six
 * seats; every other count spreads its opponents over the same span at the same step,
 * narrower when there are fewer (heads-up is the single seat at 90°) and closer when
 * there are more (nine-handed: eight at 21.7°).
 *
 * Seat `hero + 1` is the LEFTMOST (166°) and the order runs left to right across the
 * far side: that is clockwise as seen from above with the hero at the near side, which
 * is the direction the game runs (Phase 5 box 0(a)).
 */
export const MOCK_ANGLES_6 = [166, 127, 90, 53, 14] as const;
/** PHASE 7: the mock's span and step, generalised. */
export const ARC = { from: 166, to: 14, step: 38 } as const;

export function opponentAngles(opponents: number): number[] {
  if (opponents <= 0) return [];
  if (opponents === 5) return [...MOCK_ANGLES_6];
  if (opponents === 1) return [90];
  const span = ARC.from - ARC.to;
  const step = Math.min(ARC.step, span / (opponents - 1));
  const mid = (ARC.from + ARC.to) / 2;
  return Array.from({ length: opponents }, (_, j) => mid + step * ((opponents - 1) / 2 - j));
}

/**
 * Far seats sit on the rail across the table; the ones out toward the ends sit on the
 * SIDE rails, with their plate pushed out over the wood. build-mock.py: `far = 50 <= a <= 130`.
 */
export const isFar = (deg: number) => deg >= 50 && deg <= 130;

/** How much of a standing figure shows above the rail, at depth scale 1. build-mock.py: `vis = 205 * sc`. */
export const TORSO_VIS = 205;
/**
 * PHASE 7: where the rail cuts a FULL-BODY crop.
 *
 * The mock stood WAIST crops (`k_*-waist.webp`, head to waist) and showed 205 of their
 * 330 px above the rail. The shipped crops are the whole turnaround figure, hat to
 * boots, so the figure is scaled until the same 205 px sits above the rail with the
 * rail crossing it at 44 % of its height — the waistline on these renders, measured
 * off the contact sheet — and the table in front hides the rest.
 */
export const RAIL_CUT = 0.44;

export interface StandingBox {
  /** Centre x and the TOP of the figure, in stage px; its height; the depth scale. */
  cx: number; top: number; h: number; s: number;
  /** The rail line at this figure, in stage px: the figure is drawn down to here. */
  rail: number;
}

export interface SaloonSeat {
  /** Engine seat index. */
  seat: number;
  deg: number;
  /** One of the two ends of the arc: the plate goes out over the side rail, the pair beside it. */
  end: boolean;
  /** Depth scale at the seat — far seats smaller, as m11. */
  s: number;
  figure: StandingBox;
  /** The name plate on the rail, centre. */
  plate: Projected;
  /** The pair on the felt in front of them, centre. */
  cards: Projected;
  /** The bet spot, centre. */
  bet: Projected;
  /** The dealer button when this seat has it, centre. */
  button: Projected;
}

/* ------------------------------------------------------------- design boxes --- */

/**
 * PHASE 7: every piece's box at depth scale 1, in stage px — the sizes `saloon.css`
 * draws them at, so the geometry can PLACE them clear of each other rather than hope.
 *
 *   PLATE — the mock's `.name` (padding 6 x 16 round an 18px Rye name over an 18px
 *           number, about 140 x 52 for its names), FIXED at 150 x 58: the name
 *           truncates, which a `shortName` never needs.
 *   PAIR  — two cards 34 wide (build-mock.py `.hole img { width: 34px }`), 3 apart, and
 *           46 tall flat (a turned-over hand is drawn flat, the backs squashed .84).
 *   BET   — a pile two stacks wide at a 40px chip, ten chips high, and its number
 *           under it. A bigger bet draws a wider pile; this is the box that is kept
 *           clear.
 *   BUTTON — build-mock.py `.dbtn`, 40px squashed .7.
 */
export const PLATE_BOX = { w: 150, h: 58 } as const;
export const PAIR_BOX = { w: 71, h: 46 } as const;
export const BET_BOX = { w: 76, h: 70 } as const;
export const BUTTON_BOX = { w: 40, h: 28 } as const;

export interface Rect { l: number; t: number; r: number; b: number }
const box = (x: number, y: number, w: number, h: number): Rect => ({ l: x - w / 2, t: y - h / 2, r: x + w / 2, b: y + h / 2 });
export const plateRect = (p: Projected): Rect => box(p.x, p.y, PLATE_BOX.w * p.s, PLATE_BOX.h * p.s);
export const pairRect = (p: Projected): Rect => box(p.x, p.y, PAIR_BOX.w * p.s, PAIR_BOX.h * p.s);
export const betRect = (p: Projected): Rect => box(p.x, p.y, BET_BOX.w * p.s, BET_BOX.h * p.s);
export const buttonRect = (p: Projected): Rect => box(p.x, p.y, BUTTON_BOX.w * p.s, BUTTON_BOX.h * p.s);
/** Two boxes touch — with `gap` px of air required between them. */
export const overlap = (a: Rect, b: Rect, gap = 0) =>
  a.l < b.r + gap && b.l < a.r + gap && a.t < b.b + gap && b.t < a.b + gap;

/* ------------------------------------------------------------- the seats --- */

/**
 * Where each opponent stands and where their things lie, for `count` seats with the
 * hero at `heroSeat`. Pure and memoised.
 *
 * m11 drew five opponents round a three-card flop, and its numbers are kept where they
 * hold. What had to change, because the shipped table has a river, nine seats, a pile of
 * chips per bet and a button that moves — each a PHASE 7 rule, and every one of them is
 * tested for every count from 2 to 9 in `tests/saloon-geometry.test.ts`:
 *
 *   - END seats — the two ends of the arc (outside 50°–130° up to six seats, the first
 *     and last above that) — put their plate out over the side rail at 1.16 of the felt
 *     (mock: 1.12, which at nine-handed put the end plate under the next seat's) and
 *     their pair BESIDE the plate on the side toward the middle (mock: `ell(a, 0.9)`,
 *     which is beside it at 166° and under it at 147°), 30 px lower when crowded.
 *   - RAIL seats put their pair straight BELOW their plate. The mock slid it 118 px to
 *     the left to make room for a pile of the seat's stack on the right; that pile is
 *     not drawn (the stack is the number on the plate, as on the top-down felt), and in
 *     m11 itself the slid pair sat six pixels under the corner of its own plate.
 *   - BETS go out along the seat's bearing: rail seats at the mock's 0.50, pushed further
 *     out until they are clear of the board — at nine-handed the far seats' chips at
 *     0.50 are ON the flop — and end seats at 0.72 (mock 0.50: at 0.50 a side seat's
 *     chips are at stage x 567 / 1033 and a five-card board runs 532 to 1068).
 *   - The BUTTON goes to the first of seven spots round its seat's bet and pair that is
 *     clear of every plate, pair and bet and of the middle — beside the bet toward the
 *     hero first. The mock drew one button, by the hero.
 */
const BOARD_CLEAR = 6;
const PAIR_GAP = 5;
export const BET_K = { rail: 0.5, end: 0.72, max: 0.9 } as const;
export const END_PLATE_K = 1.16;
export const END_PAIR_DROP = 30;
export const END_BET_TURN = 3;

const cache = new Map<string, SaloonSeat[]>();

export function saloonSeats(count: number, heroSeat: number): SaloonSeat[] {
  const key = `${count}:${heroSeat}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const hero = heroSeat >= 0 ? heroSeat : 0;
  const angles = opponentAngles(count - 1);
  const crowded = count >= 7;
  const board = middleRects();
  const placed: (Omit<SaloonSeat, "button">)[] = [];
  angles.forEach((deg, j) => {
    const seat = (hero + 1 + j) % count;
    const end = crowded ? (angles.length >= 3 && (j === 0 || j === angles.length - 1)) : !isFar(deg);
    /* The figure: centred over the rail just outside the felt, standing so that the
       same amount of torso shows at every seat (build-mock.py: `ell(a, 1.06)`, the
       silhouette top there, `vis = 205 * sc`) — and never with its head off the top of
       the stage (PHASE 7: `FIGURE_TOP_MIN`; the mock cut the Vaquero's hat at y −11). */
    const [fx0] = ell(deg, 1.06);
    const top = project(fx0, silhouetteTop(fx0));
    const vis = Math.min(TORSO_VIS * top.s, top.y - FIGURE_TOP_MIN);
    const figure: StandingBox = { cx: top.x, top: top.y - vis, h: vis / RAIL_CUT, s: top.s, rail: top.y };
    const plate = project(...ell(deg, end ? END_PLATE_K : 1.0));
    const pr = plateRect(plate);
    let cards: Projected;
    if (end) {
      const side = deg > 90 ? 1 : -1;
      cards = {
        x: plate.x + side * ((PLATE_BOX.w + PAIR_BOX.w) / 2 * plate.s + PAIR_GAP),
        y: plate.y + (crowded ? END_PAIR_DROP * plate.s : 0), s: plate.s,
      };
    } else {
      cards = { x: plate.x, y: pr.b + PAIR_GAP + (PAIR_BOX.h / 2) * plate.s, s: plate.s };
    }
    const turn = end && crowded ? (deg > 90 ? END_BET_TURN : -END_BET_TURN) : 0;
    let k: number = end ? BET_K.end : BET_K.rail;
    let bet = project(...ell(deg + turn, k));
    while (!end && k < BET_K.max && overlap(betRect(bet), board.board, BOARD_CLEAR)) {
      k += 0.01;
      bet = project(...ell(deg + turn, k));
    }
    placed.push({ seat, deg, end, s: plate.s, figure, plate, cards, bet });
  });
  /* The buttons, once everything else has a place. */
  const fixed: Rect[] = [
    ...placed.flatMap((p) => [plateRect(p.plate), pairRect(p.cards), betRect(p.bet)]),
    ...Object.values(board),
  ];
  const out: SaloonSeat[] = placed.map((p) => {
    const hs = p.deg >= 90 ? -1 : 1;
    const s = p.bet.s, cs = p.cards.s;
    const bw = (BET_BOX.w / 2) * s, bh = (BET_BOX.h / 2) * s;
    const w = BUTTON_BOX.w * s, h = BUTTON_BOX.h * s;
    const cw = (PAIR_BOX.w / 2) * cs, ch = (PAIR_BOX.h / 2) * cs;
    const spots: [number, number][] = [
      [p.bet.x + hs * (bw + 4 + w / 2), p.bet.y + 20 * s],
      [p.bet.x - hs * (bw + 4 + w / 2), p.bet.y + 20 * s],
      [p.bet.x, p.bet.y + bh + 4 + h / 2],
      [p.cards.x + hs * (cw + 4 + w / 2), p.cards.y],
      [p.cards.x - hs * (cw + 4 + w / 2), p.cards.y],
      [p.cards.x, p.cards.y - ch - 4 - h / 2],
      [p.cards.x, p.cards.y + ch + 4 + h / 2],
    ];
    const free = spots.find(([x, y]) => !fixed.some((f) => overlap(buttonRect({ x, y, s }), f, 2))) ?? spots[0]!;
    return { ...p, button: { x: free[0], y: free[1], s } };
  });
  cache.set(key, out);
  return out;
}

/** PHASE 7: no standing figure's head above this stage y (the mock's reached −11). */
export const FIGURE_TOP_MIN = 6;

/* ------------------------------------------------------------- the hero --- */

/**
 * The player: their figure bottom-left, their plate beside it, their cards
 * bottom-centre over the near rail, their bet in front of them. All build-mock.py:
 *
 *   `.heroimg { left: -10px; bottom: -40px; height: 530px; transform: scaleX(-1) }`
 *   `.heroplate { left: 310px; bottom: 290px }`, disc 64 px
 *   `.hero { left: 600px; bottom: 22px }`, cards 170 px, −8° / +6°, the second −34 px
 *   hero bet at `project(FX − 0.17, FY + 0.16)`
 *
 * PHASE 7: the mock's hero was a WAIST crop 530 px tall; the shipped one is the whole
 * figure, so it is scaled until its head and shoulders fill the same box — the top of
 * the head where the mock's was (stage y 410) and the rail cut (`RAIL_CUT`) at the
 * mock's 530 px — and the frame's bottom edge cuts it, as it cut the mock's.
 */
/**
 * PHASE 7: the hero's bet moves from the mock's `(FX − 0.17, FY + 0.16)` to
 * `(FX − 0.13, FY + 0.10)`. At the mock's point the pile is at stage y 626 and the
 * hero's own ace reaches up to about 640 — in m11 the chip's number is under the card
 * (the red chip above the ace has no "400") — and an all-in pile four stacks wide runs
 * into the player's own plate. Up and in, it clears both, and the pot pill.
 */
export const HERO_BET = { dx: -0.13, dy: 0.10 } as const;

export const HERO = {
  figure: { left: -10, top: 410, h: 530 / RAIL_CUT },
  plate: { left: 310, bottom: 290 },
  cards: { cx: 600, bottom: 22, w: 170 },
  bet: project(FELT.fx + HERO_BET.dx, FELT.fy + HERO_BET.dy),
  /**
   * PHASE 7: where the player's hand turns over when they take Show — at their own
   * figure, over the chest, left of their plate: the one place on the stage that is
   * theirs and nobody else's. (Above the plate is where the first seat's bet lands.)
   */
  shown: { x: 205, y: 612, s: 1.04 },
  /**
   * The hero's button: the mock's own disc, `ell(-42, 0.86)`, brought in to 0.80. At
   * 0.86 it sits at stage y 680 and the shared action bar — taller than the mock's —
   * starts at about 690; at 0.80 there are ten pixels of felt between them.
   */
  button: project(...ell(-42, 0.8)),
} as const;

/** The middle of the table. build-mock.py: board `project(FX, FY − .02)`, pot pile `(FX + .21, FY + .15)`, pot pill `(FX + .01, FY + .16)`. */
export const MIDDLE = {
  board: project(FELT.fx, FELT.fy - 0.02),
  potChips: project(FELT.fx + 0.21, FELT.fy + 0.15),
  potPill: project(FELT.fx + 0.01, FELT.fy + 0.16),
  /**
   * PHASE 7: the deck — the mock has none. On the felt between the pot pill and the
   * pot's chips, where the dealer's hands would be and where no seat's bet can land.
   */
  deck: project(FELT.fx + 0.12, FELT.fy + 0.1),
} as const;

/** The middle's boxes, for the placement rules: the board (five cards), the pot. */
export function middleRects(): Record<"board" | "pill" | "potChips" | "heroPlate" | "heroCards" | "heroBet" | "actions" | "deck", Rect> {
  const b = MIDDLE.board;
  return {
    board: box(b.x, b.y, (5 * 96 + 4 * 14) * b.s, 96 * 1.349 * 0.84 * b.s),
    pill: box(MIDDLE.potPill.x, MIDDLE.potPill.y, 160, 42),
    potChips: { l: MIDDLE.potChips.x - 75, t: MIDDLE.potChips.y - 58, r: MIDDLE.potChips.x + 75, b: MIDDLE.potChips.y + 12 },
    heroPlate: { l: HERO.plate.left, t: 900 - HERO.plate.bottom - 88, r: HERO.plate.left + 222, b: 900 - HERO.plate.bottom },
    heroCards: { l: HERO.cards.cx - 175, t: 900 - HERO.cards.bottom - 238, r: HERO.cards.cx + 175, b: 900 },
    heroBet: box(HERO.bet.x, HERO.bet.y, 134, 72),
    actions: { l: 1600 - ACTIONS.right - ACTIONS.w, t: 690, r: 1600 - ACTIONS.right, b: 900 - ACTIONS.bottom },
    deck: box(MIDDLE.deck.x, MIDDLE.deck.y, 34 * MIDDLE.deck.s, 46 * MIDDLE.deck.s),
  };
}

/** The action bar's box, build-mock.py `.actions { right: 28px; bottom: 28px; width: 600px }`. */
export const ACTIONS = { right: 28, bottom: 28, w: 600 } as const;
